
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// module components
import UpdateTemplatesModal from '@/modules/NotificationsModule/components/UpdateTemplatesModal/index.vue'
import UpdateCompaniesModal from '@/modules/NotificationsModule/components/UpdateCompaniesModal/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { NotificationItSettingT } from '@/core/types/settings.types'

// composable
import useAccount from '@/common/composable/useAccount'
import useNotifications from '@/common/composable/useNotifications'

// constants
import { NotificationItColumns } from '@/core/constants/columns/Notifications.columns'

// realisations
import { settings } from '@/core/realisations/settings.realisation'

// @helpers
import cut from '@/common/helpers/cut'

export default defineComponent({
    components: {
        PageTitle,
        FailedFetch,
        TheButton,
        NewTable,
        NewPagination,
        NewLimit,
        Loader,
        UpdateTemplatesModal,
        UpdateCompaniesModal,
    },
    setup() {
        const { havePermissions } = useAccount()
        const notifications = useNotifications()

        const state = reactive({
            is_active_loading: false,
            limit: 50,
            settings: new DataState<NotificationItSettingT>({}),
        })

        const state_update_template = reactive({
            id: null as number | null,
            is_open: false,
            is_loading: false,
        })

        const state_update_companies = reactive({
            id: null as number | null,
            is_open: false,
            is_loading: false,
        })

        const getCompanies = (companies: { name: string }[]) => {
            if (companies) {
                const companies_names = companies.map((company) => company.name)
                return companies_names.join(', ')
            }

            return ''
        }

        const loadSettings = async (page: number) => {
            state.settings.pagination.current_page = page

            try {
                state.settings.is_loading = true

                const data = await settings.getNotificationsItList(page, state.limit)

                state.settings.list = data.data.map((notification) => {
                    return {
                        ...notification,
                        companiesString: notification.companies ? getCompanies(notification.companies) : '',
                    }
                })

                state.settings.pagination.total_pages = data.meta.last_page
                state.settings.pagination.total_items = data.meta.total

                state.settings.is_failed = false
            } catch (error) {
                state.settings.failure.catchError(error)
                state.settings.is_failed = true
            } finally {
                state.settings.is_loading = false
            }
        }

        const toggleTemplate = (row?: NotificationItSettingT) => {
            if (row) {
                state_update_template.id = row.id
            }

            state_update_template.is_open = !state_update_template.is_open
        }

        const toggleCompany = (row?: NotificationItSettingT) => {
            if (row) {
                state_update_companies.id = row.id
            }

            state_update_companies.is_open = !state_update_companies.is_open
        }

        const onLimitChange = (limit: number) => {
            state.limit = limit
            loadSettings(state.settings.pagination.current_page)
        }

        const onChangePage = (page: number) => {
            state.settings.pagination.current_page = page
            loadSettings(page)
        }

        const getCheapClass = (status: string) => {
            let class_name = 'small max-w-min '
            class_name += status === 'Доступен' ? 'green' : 'red'

            return class_name
        }

        const getActiveBtnText = (row: NotificationItSettingT) => {
            return row.isActive ? 'Выключить' : 'Включить'
        }

        const enableSetting = async (row: NotificationItSettingT) => {
            const answer = confirm('Вы действительно хотите включить настройку?')
            if (!answer) return

            try {
                state.is_active_loading = true
                await settings.enableNotificationIt(row.id)
                loadSettings(1)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При включении настройки произошла ошибка',
                })
            } finally {
                state.is_active_loading = false
            }
        }

        const disableSetting = async (row: NotificationItSettingT) => {
            const answer = confirm('Вы действительно хотите выключить настройку?')
            if (!answer) return

            try {
                state.is_active_loading = true
                await settings.disableNotificationIt(row.id)
                loadSettings(1)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При выключении настройки произошла ошибка',
                })
            } finally {
                state.is_active_loading = false
            }
        }

        const manageActiveBtn = (row: NotificationItSettingT) => {
            if (row.isActive) disableSetting(row)
            else enableSetting(row)
        }

        const onTemplateUpdated = () => {
            toggleTemplate()
            loadSettings(1)
        }

        //

        const isCanEditTemplates = computed(() =>
            havePermissions(['notification.templates.update'])
        )
        const isCanEditCompanies = computed(() =>
            havePermissions(['notification.company-settings.update'])
        )

        //

        onBeforeMount(() => {
            loadSettings(1)
        })

        //

        return {
            state,
            state_update_template,
            state_update_companies,
            //
            loadSettings,
            toggleTemplate,
            toggleCompany,
            onLimitChange,
            onChangePage,
            getCheapClass,
            getCompanies,
            havePermissions,
            //
            isCanEditTemplates,
            isCanEditCompanies,
            getActiveBtnText,
            manageActiveBtn,
            cut,
            onTemplateUpdated,
            //
            NotificationItColumns,
        }
    },
})
